import React from "react";
import { useParams, Link , useNavigate } from "react-router-dom";
import industriesData from "./IndustriesData";

// ─── Placeholder images per industry (swap with real ones later) ───────────────
const industryImages = {
  default: {
    hero: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&q=80",
    gallery: [
      "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&q=80",
      "https://images.unsplash.com/photo-1551434678-e076c223a692?w=600&q=80",
      "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&q=80",
    ],
  },
};


// ─── Pick icon SVG by index for variety ───────────────────────────────────────
const StatIcon = ({ type }) => {
  const icons = {
    impact: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    growth: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M3 3v18h18M7 16l4-4 4 4 4-4" />
      </svg>
    ),
    team: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M17 20h5v-2a4 4 0 00-5-3.87M9 20H4v-2a4 4 0 015-3.87m6-4a4 4 0 11-8 0 4 4 0 018 0zm6 4a2 2 0 11-4 0 2 2 0 014 0zM5 16a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    solution: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
  };
  return icons[type] || icons.impact;
};

// ─── Why Choose Us items ──────────────────────────────────────────────────────
const whyChooseUs = [
  {
    icon: "solution",
    title: "Tailored Solutions",
    description:
      "We don't offer generic products. Every solution is designed around your industry's unique challenges and workflows.",
  },
  {
    icon: "team",
    title: "Domain Experts",
    description:
      "Our teams include specialists who have worked inside your industry — giving us firsthand understanding of what matters.",
  },
  {
    icon: "growth",
    title: "Proven Growth",
    description:
      "We've helped organisations achieve measurable results — from operational efficiency gains to revenue growth.",
  },
  {
    icon: "impact",
    title: "End-to-End Delivery",
    description:
      "From discovery and design through to deployment and support — we own the entire journey alongside you.",
  },
];

// ─── Main Component ───────────────────────────────────────────────────────────
const IndustryDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Find matching industry by slug
  const industry = industriesData.find((ind) => ind.slug === slug);

  // Images: use per-industry override or default
  const images = industryImages[slug] || industryImages.default;

  // ── Not found state ─────────────────────────────────────────────────────────
  if (!industry) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <div className="text-center p-8 max-w-md mx-4">
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
            <svg className="w-12 h-12 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Industry Not Found</h1>
          <p className="text-gray-600 mb-8">
            The industry page you're looking for doesn't exist or has been moved.
          </p>
          <Link
            to="/industries"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Browse All Industries
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">

      {/* ── Hero Section ──────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        {/* Hero Image */}
        <div className="relative h-[480px] md:h-[580px] w-full">
          <img
            src={images.hero}
            alt={industry.title}
            className="w-full h-full object-cover"
          />
          {/* Dark overlay with gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/80 via-purple-900/60 to-pink-900/40" />

          {/* Breadcrumb */}
          <div className="absolute top-6 left-0 right-0">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <nav className="flex items-center gap-2 text-sm text-white/70">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <span>/</span>
                <Link to="/industries" className="hover:text-white transition-colors">Industries</Link>
                <span>/</span>
                <span className="text-white font-medium">{industry.title}</span>
              </nav>
            </div>
          </div>

          {/* Hero Text */}
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 text-white text-sm font-medium mb-6">
                  <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                  Industry Solutions
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight">
                  {industry.title}
                </h1>
                <p className="text-white/85 text-lg md:text-xl leading-relaxed mb-8 max-w-xl">
                  {industry.description[0]}
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link
                    to="/contact"
                    onClick={()=>window.scrollTo({top :0, behavior : "smooth"})}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-xl shadow-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-300"
                  >
                    Get a Free Consultation
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </Link>
                  <Link
                    to="/industries"
                    onClick={()=>window.scrollTo({top :0, behavior : "smooth"})}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white/15 backdrop-blur-sm border border-white/30 text-white font-medium rounded-xl hover:bg-white/25 transition-all duration-300"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    All Industries
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        {/* <div className="bg-white border-b border-gray-100 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-3 divide-x divide-gray-100">
              <div className="py-5 px-6 text-center">
                <div className="text-2xl font-extrabold text-indigo-600">
                  {industry.impact.length}+
                </div>
                <div className="text-sm text-gray-500 font-medium mt-0.5">Impact Areas</div>
              </div>
              <div className="py-5 px-6 text-center">
                <div className="text-2xl font-extrabold text-purple-600">
                  {industry.description.length * 3}+
                </div>
                <div className="text-sm text-gray-500 font-medium mt-0.5">Projects Delivered</div>
              </div>
              <div className="py-5 px-6 text-center">
                <div className="text-2xl font-extrabold text-pink-600">100%</div>
                <div className="text-sm text-gray-500 font-medium mt-0.5">Client Satisfaction</div>
              </div>
            </div>
          </div>
        </div> */}
      </section>

      {/* ── Overview Section ──────────────────────────────────────────────────── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Text */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-bold uppercase tracking-widest mb-5">
                About This Industry
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-6 leading-tight">
                How We Serve the{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                  {industry.title}
                </span>{" "}
                Sector
              </h2>
              <div className="space-y-4">
                {industry.description.map((para, i) => (
                  <p key={i} className="text-gray-600 leading-relaxed text-base">
                    {para}
                  </p>
                ))}
              </div>
            </div>

            {/* Gallery grid */}
          <div className="">
            <img
                  src={industry.img}
                  alt={`${industry.title} overview`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
          </div>
          </div>
        </div>
      </section>

      {/* ── Impact Areas Section ─────────────────────────────────────────────── */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-indigo-200 text-indigo-700 text-xs font-bold uppercase tracking-widest mb-4">
              Our Impact
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
              How We Create Impact in{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                {industry.title}
              </span>
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Every engagement we take on is focused on delivering measurable, lasting impact across the following areas.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {industry.impact.map((item, idx) => (
              <div
                key={idx}
                className="group relative bg-white rounded-2xl p-5 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-indigo-200 overflow-hidden"
              >
                {/* Hover gradient top bar */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 rounded-t-2xl" />

                <div className="flex items-start gap-4">
                  {/* Number badge */}
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-sm font-bold shadow-md group-hover:scale-110 transition-transform duration-300">
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-800 font-semibold leading-snug group-hover:text-indigo-700 transition-colors">
                      {item.trim()}
                    </p>
                  </div>
                  {/* Arrow */}
                  <svg className="w-5 h-5 text-gray-300 group-hover:text-indigo-500 flex-shrink-0 transition-colors mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why Choose Us ─────────────────────────────────────────────────────── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-50 border border-purple-200 text-purple-700 text-xs font-bold uppercase tracking-widest mb-4">
              Why Us
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
              Why Businesses Choose Us
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              We combine deep industry knowledge with cutting-edge technology to deliver outcomes that matter.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, idx) => (
              <div
                key={idx}
                className="group relative bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 border border-gray-100 hover:border-purple-200 hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 opacity-0 group-hover:opacity-5 transition-opacity duration-300 rounded-2xl" />
                <div className="relative">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-indigo-600 mb-5 group-hover:scale-110 transition-transform duration-300 shadow-sm">
                    <StatIcon type={item.icon} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-indigo-700 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Key Benefits ──────────────────────────────────────────────────────── */}
      <section className="py-16 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl h-80 md:h-96">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80"
                alt="Benefits"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-indigo-900/50 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg max-w-xs">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></div>
                    <span className="text-sm font-bold text-gray-800">Actively Delivering</span>
                  </div>
                  <p className="text-xs text-gray-500">Trusted by industry leaders worldwide</p>
                </div>
              </div>
            </div>

            {/* Benefits list */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-indigo-200 text-indigo-700 text-xs font-bold uppercase tracking-widest mb-5">
                Key Benefits
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-8 leading-tight">
                What You Get When You{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                  Work With Us
                </span>
              </h2>
              <div className="space-y-4">
                {[
                  "Competitive and transparent pricing models",
                  "Dedicated team with industry-specific expertise",
                  "Continuous learning, innovation & R&D investment",
                  "Agile delivery — fast iterations, early value",
                  "Post-delivery support and long-term partnerships",
                  "Measurable ROI on every engagement",
                ].map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3 group">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center mt-0.5 shadow-sm group-hover:scale-110 transition-transform">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-700 font-medium group-hover:text-indigo-700 transition-colors">
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Related Industries ────────────────────────────────────────────────── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                Explore Other Industries
              </h2>
              <p className="text-gray-500 mt-1">Discover more sectors we serve</p>
            </div>
            <Link
              to="/industries"
              onClick={()=>window.scrollTo({top :0, behavior : "smooth"})}
              className="hidden sm:inline-flex items-center gap-2 text-indigo-600 font-semibold hover:text-indigo-800 transition-colors text-sm"
            >
              View All
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {industriesData
              .filter((ind) => ind.slug !== slug)
              .slice(0, 3)
              .map((ind, idx) => (
                <Link
                  key={idx}
                  to={`/industries/${ind.slug}`}
                  onClick={()=>window.scrollTo({top :0, behavior : "smooth"})}
                  className="group relative bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden border border-gray-100"
                >
                  {/* Gradient border on hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl" />
                  <div className="relative bg-white rounded-2xl m-px p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-md group-hover:scale-110 transition-transform duration-300">
                        {idx + 1}
                      </div>
                      <svg className="w-5 h-5 text-gray-300 group-hover:text-purple-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-indigo-600 transition-colors">
                      {ind.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed line-clamp-2">
                      {ind.description[0]}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {ind.impact.slice(0, 2).map((role, i) => (
                        <span key={i} className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-xs rounded-full font-medium">
                          {role.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* ── CTA Section ───────────────────────────────────────────────────────── */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-3xl p-8 md:p-14 text-center text-white overflow-hidden">
            {/* Decorative blobs */}
            <div className="absolute -top-10 -left-10 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
            <div className="absolute -bottom-10 -right-10 w-52 h-52 bg-white/10 rounded-full blur-2xl" />

            <div className="relative">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/20 border border-white/30 text-white text-xs font-bold uppercase tracking-widest mb-6">
                Ready to Get Started?
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold mb-4 leading-tight">
                Transform Your{" "}
                <span className="text-yellow-300">{industry.title}</span>{" "}
                Operations Today
              </h2>
              <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto leading-relaxed">
                Let's discuss how our industry-specific solutions can help you achieve
                measurable outcomes and sustainable growth.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  onClick={()=>window.scrollTo({top :0, behavior : "smooth"})}
                  className="inline-flex items-center justify-center gap-2 px-8 py-3.5 text-base font-bold rounded-xl bg-white text-indigo-700 hover:bg-gray-100 transition-colors shadow-xl hover:shadow-2xl"
                >
                  Get Free Consultation
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
                <Link
                  to="/industries"
                  onClick={()=>window.scrollTo({top :0, behavior : "smooth"})}
                  className="inline-flex items-center justify-center gap-2 px-8 py-3.5 text-base font-semibold rounded-xl bg-white/15 border border-white/30 text-white hover:bg-white/25 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  All Industries
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default IndustryDetails;